import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
} from '@mui/material';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { TimeslotsService } from '../../api/services/timeslotsService';
import { ITimeslot } from '../../types/api/response/timeslotresponse';

const formatTime = (timeStr: string): string => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h = hours % 12 || 12;
  return `${h}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

interface SlotSectionProps {
  title: string;
  slots: ITimeslot[];
  togglingId: number | null;
  onToggle: (slot: ITimeslot, value: boolean) => void;
}

const SlotSection = ({ title, slots, togglingId, onToggle }: SlotSectionProps) => (
  <Card variant="outlined">
    <CardHeader
      title={
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
      }
      sx={{ pb: 1 }}
    />
    <Divider />
    <CardContent sx={{ p: 0 }}>
      <List disablePadding>
        {slots.map((slot, idx) => (
          <Box key={slot.pk_id}>
            <ListItem
              sx={{ px: 3, py: 1 }}
              secondaryAction={
                togglingId === slot.pk_id ? (
                  <CircularProgress size={20} />
                ) : (
                  <Switch
                    checked={slot.slot_isactive}
                    onChange={(e) => onToggle(slot, e.target.checked)}
                    color="success"
                    size="small"
                  />
                )
              }
            >
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    color={slot.slot_isactive ? 'text.primary' : 'text.disabled'}
                  >
                    {formatTime(slot.slot_start)} – {formatTime(slot.slot_end)}
                  </Typography>
                }
              />
            </ListItem>
            {idx < slots.length - 1 && <Divider />}
          </Box>
        ))}
        {slots.length === 0 && (
          <ListItem sx={{ px: 3, py: 2 }}>
            <ListItemText
              primary={
                <Typography variant="body2" color="text.disabled">
                  No slots available
                </Typography>
              }
            />
          </ListItem>
        )}
      </List>
    </CardContent>
  </Card>
);

export const DeliverySlotPage = () => {
  const dispatch = useDispatch();
  const [todaySlots, setTodaySlots] = useState<ITimeslot[]>([]);
  const [tomorrowSlots, setTomorrowSlots] = useState<ITimeslot[]>([]);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await TimeslotsService.getAllTimeslots();
      setTodaySlots(res.todaySlots ?? []);
      setTomorrowSlots(res.tomorrowSlots ?? []);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to load time slots. ${e?.response?.data || ''}`,
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (slot: ITimeslot, value: boolean) => {
    setTogglingId(slot.pk_id);
    try {
      await TimeslotsService.updateTimeslot({
        id: slot.pk_id,
        active: value,
      });
      const applyUpdate = (slots: ITimeslot[]) =>
        slots.map((s) => (s.pk_id === slot.pk_id ? { ...s, slot_isactive: value } : s));
      if (slot.slots_day === 'today') {
        setTodaySlots((prev) => applyUpdate(prev));
      } else {
        setTomorrowSlots((prev) => applyUpdate(prev));
      }
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to update slot. ${e?.response?.data || ''}`,
        }),
      );
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Delivery Time Slots
      </Typography>

      <Box display="flex" flexDirection="row" gap={3} alignItems="flex-start">
        <Box flex={1} minWidth={0}>
          <SlotSection
            title="Today"
            slots={todaySlots}
            togglingId={togglingId}
            onToggle={handleToggle}
          />
        </Box>
        <Box flex={1} minWidth={0}>
          <SlotSection
            title="Tomorrow"
            slots={tomorrowSlots}
            togglingId={togglingId}
            onToggle={handleToggle}
          />
        </Box>
      </Box>
    </Box>
  );
};
